import { UPDATE_ORGANISATION_AI_SETTINGS } from "@lib/graphql/mutations";
import { ORGANISATION_SETTINGS_QUERY } from "@lib/graphql/queries";
import { useState } from "react";
import { useMutation, useQuery } from "urql";

interface ModelOption {
  id: string;
  label: string;
}

interface ModelGroup {
  label: string;
  models: ModelOption[];
}

export function AiSettings() {
  const [{ data }, reexecute] = useQuery({ query: ORGANISATION_SETTINGS_QUERY });
  const [, updateAiSettings] = useMutation(UPDATE_ORGANISATION_AI_SETTINGS);

  const org = data?.currentOrganisation;
  const isAdmin = org?.currentUserRole === "admin";
  const savedPositive = (org?.aiPositiveInstructions as string) || "";
  const savedNegative = (org?.aiNegativeInstructions as string) || "";
  const savedModel = (org?.llmModel as string) || "";
  const modelGroups: ModelGroup[] = org?.aiAvailableModels || [];

  const [positive, setPositive] = useState(savedPositive);
  const [negative, setNegative] = useState(savedNegative);
  const [model, setModel] = useState(savedModel);
  const [positiveDirty, setPositiveDirty] = useState(false);
  const [negativeDirty, setNegativeDirty] = useState(false);
  const [modelDirty, setModelDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  if (!positiveDirty && positive !== savedPositive) {
    setPositive(savedPositive);
  }
  if (!negativeDirty && negative !== savedNegative) {
    setNegative(savedNegative);
  }
  if (!modelDirty && model !== savedModel) {
    setModel(savedModel);
  }

  function handlePositiveChange(value: string) {
    setPositive(value);
    setPositiveDirty(true);
    setSuccess(false);
  }

  function handleNegativeChange(value: string) {
    setNegative(value);
    setNegativeDirty(true);
    setSuccess(false);
  }

  function handleModelChange(value: string) {
    setModel(value);
    setModelDirty(true);
    setSuccess(false);
  }

  async function handleSave() {
    setSaving(true);
    setErrors([]);
    setSuccess(false);

    const input: {
      aiPositiveInstructions?: string;
      aiNegativeInstructions?: string;
      llmModel?: string;
    } = {};
    if (positiveDirty) input.aiPositiveInstructions = positive;
    if (negativeDirty) input.aiNegativeInstructions = negative;
    if (modelDirty) input.llmModel = model;

    const result = await updateAiSettings({ input });
    const mutationErrors = result.data?.updateOrganisationAiSettings?.errors || [];

    if (mutationErrors.length === 0) {
      setPositiveDirty(false);
      setNegativeDirty(false);
      setModelDirty(false);
      setSuccess(true);
      reexecute({ requestPolicy: "network-only" });
    } else {
      setErrors(mutationErrors);
    }
    setSaving(false);
  }

  const dirty = positiveDirty || negativeDirty || modelDirty;

  return (
    <div className="mt-6 space-y-8">
      <section>
        <h2 className="text-lg font-semibold text-text-primary">AI Model</h2>
        <p className="mt-1 text-sm text-text-muted">
          The model that powers AI features for your organisation — the platform is implied by the
          model. Options are limited by your data residency requirement. Existing chat threads stay
          on the model they were started with and can no longer be continued after a change.
        </p>

        <div className="mt-3">
          <select
            id="org-llm-model"
            value={model}
            disabled={!isAdmin}
            onChange={(e) => handleModelChange(e.target.value)}
            className="w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
          >
            {modelGroups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.models.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-text-primary">AI Guidance</h2>
        <p className="mt-1 text-sm text-text-muted">
          Persistent rules that are injected into AI interactions for your organisation. They appear
          in the chat agent&apos;s system prompt and in MCP tool responses for external AI clients.
        </p>

        <div className="mt-3">
          <label className="block text-sm font-medium text-text-primary" htmlFor="ai-positive">
            Always do
          </label>
          <textarea
            id="ai-positive"
            value={positive}
            onChange={(e) => handlePositiveChange(e.target.value)}
            disabled={!isAdmin}
            rows={6}
            placeholder="e.g. Always use formal British English. Always cite sources for any client-facing recommendation."
            className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary placeholder:text-text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
          />
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-text-primary" htmlFor="ai-negative">
            Never do
          </label>
          <textarea
            id="ai-negative"
            value={negative}
            onChange={(e) => handleNegativeChange(e.target.value)}
            disabled={!isAdmin}
            rows={6}
            placeholder="e.g. Never use emoji in any document or chat. Never speculate beyond the evidence on record."
            className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary placeholder:text-text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
          />
        </div>
      </section>

      {errors.length > 0 && (
        <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
          {errors.map((error) => (
            <p key={error} className="text-sm text-red-700 dark:text-red-400">
              {error}
            </p>
          ))}
        </div>
      )}
      {success && <p className="text-sm text-green-600 dark:text-green-400">AI settings saved.</p>}

      {isAdmin && (
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save AI Settings"}
        </button>
      )}
    </div>
  );
}
